import React, { useState, useRef } from "react";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import { logout } from "../Slices/auth";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Logo from "../assets/logo.png";
import Avatar from "@mui/material/Avatar";
import useMediaQuery from "@mui/material/useMediaQuery";
import { styled, useTheme } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Menu, MenuItem, Tooltip } from "@mui/material";
import { WcagBox } from "./utilities/WcagBox";
import AccessibilityIcon from "@mui/icons-material/Accessibility";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";

const drawerWidth = 240;

const pages = [
  { name: "Moje CV", page: "/yourcv" },
  { name: "Ścieżki kompetencyjne", page: "/sciezki" },
  { name: "Odznaki", page: "/odznaki" },
  { name: "Certyfikaty", page: "/certificates" },
  { name: "Kwalifikacje", page: "/qualifications" },
  {
    name: "VR",
    page: "/vr",
    submenu: [
      { name: "Grupy", page: "/groups" },
      { name: "Raporty", page: "/reports" },
      { name: "Kalendarz", page: "/vr/callendar" },
      { name: "Kreator scenariuszy", page: "/vr/scenarios" },
      { name: "Vr Cup 2026", page: "/vr-cup" },
      { name: "Turnieje", page: "/vr-cups" },
    ],
  },
];

const guestPages = [
  { name: "O Fundacji", page: "/" },
  { name: "Kwalifikacje", page: "/qualifications" },
  { name: "Twoje CV", page: "/yourcv" },
];

const AppBarNav = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const theme = useTheme();

  const wcagRef = useRef(null);
  const wcagButtonRef = useRef(null);
  const menuButtonRefs = useRef({});

  const [open, setOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const mobileWidth = useMediaQuery("(max-width:900px)");

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleWcagClick = () => {
    if (wcagRef.current) {
      wcagRef.current.callChildFunction({
        buttonRef: wcagButtonRef.current,
      });
    }
  };

  const handleMenuOpen = (name, event) => {
    setActiveMenu(name);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setActiveMenu(null);
    setAnchorEl(null);
  };

  const handleMenuKeyDown = (event, name) => {
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "ArrowDown"
    ) {
      event.preventDefault();

      setActiveMenu(name);
      setAnchorEl(event.currentTarget);

      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      handleMenuClose();
    }
  };

  const handleSubmenuKeyDown = (event, page) => {
    if (event.key === "Escape") {
      event.preventDefault();

      handleMenuClose();

      requestAnimationFrame(() => {
        menuButtonRefs.current[page]?.focus();
      });
    }
  };

  const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-start",
  }));

  return (
    <>
      <AppBar className="app-bar" position={mobileWidth ? "fixed" : "relative"}>
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Link
              to="/"
              aria-label="Micree - strona główna"
              style={{
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <img
                src={Logo}
                alt="Micree"
                style={{
                  maxWidth: "100px",
                  marginRight: "30px",
                  display: "block",
                }}
              />
            </Link>
          </Box>

          <Box
            component="nav"
            aria-label="Główna nawigacja"
            sx={{
              display: { xs: "none", md: "flex" },
              mr: 2,
              alignItems: "center",
            }}
          >
            {user &&
              (user.userRole === "user" ||
                user.userRole === "developer" ||
                user.userRole === "admin") &&
              pages.map(({ name, page, submenu }) => {
                if (!submenu) {
                  return (
                    <Button
                      key={page}
                      component={Link}
                      to={page}
                      sx={{
                        color: "white",
                        minHeight: "48px",
                        px: 2,
                        "&:focus-visible": {
                          outline: "3px solid",
                          outlineColor: theme.palette.texts.primary,
                          outlineOffset: "2px",
                        },
                      }}
                    >
                      {name}
                    </Button>
                  );
                }

                const menuId = `menu-${page.replace(/\//g, "-")}`;
                const buttonId = `menu-button-${page.replace(/\//g, "-")}`;

                return (
                  <Box
                    key={page}
                    sx={{
                      display: "inline-flex",
                      position: "relative",
                    }}
                    onMouseEnter={(event) => {
                      handleMenuOpen(name, event);
                    }}
                    onMouseLeave={() => {
                      handleMenuClose();
                    }}
                  >
                    <Button
                      ref={(element) => {
                        menuButtonRefs.current[page] = element;
                      }}
                      id={buttonId}
                      aria-haspopup="menu"
                      aria-expanded={activeMenu === name}
                      aria-controls={activeMenu === name ? menuId : undefined}
                      sx={{
                        color: "white",
                        minHeight: "48px",
                        px: 2,
                        "&:focus-visible": {
                          outline: "3px solid",
                          outlineColor: theme.palette.texts.primary,
                          outlineOffset: "2px",
                        },
                      }}
                      onClick={(event) => {
                        if (activeMenu === name) {
                          handleMenuClose();
                        } else {
                          handleMenuOpen(name, event);
                        }
                      }}
                      onKeyDown={(event) => {
                        handleMenuKeyDown(event, name);
                      }}
                    >
                      {name}
                    </Button>

                    <Menu
                      id={menuId}
                      anchorEl={anchorEl}
                      open={activeMenu === name}
                      onClose={handleMenuClose}
                      MenuListProps={{
                        "aria-labelledby": buttonId,
                        onKeyDown: (event) => {
                          handleSubmenuKeyDown(event, page);
                        },
                        onMouseEnter: () => {
                          setActiveMenu(name);
                        },
                      }}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                    >
                      {submenu.map(({ name: subName, page: subPage }) => (
                        <MenuItem
                          key={subPage}
                          component={Link}
                          to={subPage}
                          onClick={handleMenuClose}
                          sx={{
                            minHeight: "44px",
                            "&:focus-visible": {
                              outline: "3px solid",
                              outlineColor: theme.palette.primary.main,
                              outlineOffset: "-3px",
                            },
                          }}
                        >
                          {subName}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                );
              })}

            {user &&
              user.userRole === "guest" &&
              guestPages.map(({ name, page }) => (
                <Button
                  key={page}
                  component={Link}
                  to={page}
                  sx={{
                    color: "white",
                    minHeight: "48px",
                    px: 2,
                    "&:focus-visible": {
                      outline: "3px solid",
                      outlineColor: theme.palette.texts.primary,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  {name}
                </Button>
              ))}
          </Box>

          {!mobileWidth && (
            <IconButton
              ref={wcagButtonRef}
              size="large"
              aria-label="Ustawienia dostępności WCAG"
              aria-haspopup="dialog"
              color={theme.palette.texts.primary}
              sx={{
                ml: 1,
                width: "48px",
                height: "48px",
                border: "2px solid",
                borderColor: theme.palette.texts.primary,
                borderRadius: "8px",
                "&:focus-visible": {
                  outline: "3px solid",
                  outlineColor: theme.palette.texts.primary,
                  outlineOffset: "3px",
                },
              }}
              onClick={handleWcagClick}
            >
              <AccessibilityIcon
                sx={{
                  color: theme.palette.texts.primary,
                }}
              />
            </IconButton>
          )}

          {!mobileWidth && user && (
            <Link
              to="/profile"
              aria-label={`Profil: ${user.firstname} ${user.lastname}`}
              style={{
                textDecoration: "none",
              }}
            >
              <Tooltip title={`${user.firstname} ${user.lastname}`}>
                <IconButton
                  size="small"
                  sx={{
                    ml: 1,
                    "&:focus-visible": {
                      outline: "3px solid",
                      outlineColor: theme.palette.texts.primary,
                      outlineOffset: "3px",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                    }}
                    alt={`${user.firstname} ${user.lastname}`}
                    src={user.profileImage}
                  />
                </IconButton>
              </Tooltip>
            </Link>
          )}

          {!mobileWidth && (
            <IconButton
              color="inherit"
              onClick={handleLogout}
              aria-label="Wyloguj"
              sx={{
                ml: 1,
                "&:focus-visible": {
                  outline: "3px solid",
                  outlineColor: theme.palette.texts.primary,
                  outlineOffset: "3px",
                },
              }}
            >
              <LogoutIcon color="fontWhite" />
            </IconButton>
          )}

          {mobileWidth && (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label={open ? "Zamknij menu" : "Otwórz menu"}
              aria-expanded={open}
              aria-controls="mobile-navigation"
              sx={{
                mr: 0,
                "&:focus-visible": {
                  outline: "3px solid",
                  outlineColor: theme.palette.texts.primary,
                  outlineOffset: "3px",
                },
              }}
              onClick={open ? handleDrawerClose : handleDrawerOpen}
            >
              {open ? <ChevronRightIcon /> : <MenuIcon />}
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        <Drawer
          id="mobile-navigation"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
            },
          }}
          variant="persistent"
          anchor="right"
          open={open}
        >
          <DrawerHeader>
            <IconButton
              onClick={handleDrawerClose}
              aria-label="Zamknij menu"
              sx={{
                "&:focus-visible": {
                  outline: "3px solid",
                  outlineColor: theme.palette.primary.main,
                  outlineOffset: "2px",
                },
              }}
            >
              {theme.direction === "rtl" ? (
                <ChevronLeftIcon />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </DrawerHeader>

          <Divider />

          <List component="nav" aria-label="Główna nawigacja" sx={{ px: 1 }}>
            {user &&
              (user.userRole === "user" || user.userRole === "admin") &&
              pages.map(({ name, page }) => (
                <ListItemButton
                  key={page}
                  component={Link}
                  to={page}
                  onClick={handleDrawerClose}
                  sx={{
                    my: 0.5,
                    minHeight: "48px",
                    color: "#474c55",
                    borderRadius: "8px",
                    "&:focus-visible": {
                      outline: "3px solid",
                      outlineColor: theme.palette.primary.main,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  <ListItemText primary={name} />
                </ListItemButton>
              ))}

            {user &&
              user.userRole === "guest" &&
              guestPages.map(({ name, page }) => (
                <ListItemButton
                  key={page}
                  component={Link}
                  to={page}
                  onClick={handleDrawerClose}
                  sx={{
                    my: 0.5,
                    minHeight: "48px",
                    color: "#474c55",
                    borderRadius: "8px",
                    "&:focus-visible": {
                      outline: "3px solid",
                      outlineColor: theme.palette.primary.main,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  <ListItemText primary={name} />
                </ListItemButton>
              ))}
          </List>

          <Divider />

          <List>
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/profile"
                onClick={handleDrawerClose}
                sx={{
                  minHeight: "48px",
                  "&:focus-visible": {
                    outline: "3px solid",
                    outlineColor: theme.palette.primary.main,
                    outlineOffset: "2px",
                  },
                }}
              >
                <ListItemIcon>
                  <ManageAccountsIcon />
                </ListItemIcon>

                <ListItemText primary="Twój profil" />
              </ListItemButton>
            </ListItem>

            {mobileWidth && (
              <ListItem disablePadding>
                <ListItemButton
                  onClick={handleWcagClick}
                  sx={{
                    minHeight: "48px",
                    "&:focus-visible": {
                      outline: "3px solid",
                      outlineColor: theme.palette.primary.main,
                      outlineOffset: "2px",
                    },
                  }}
                >
                  <ListItemIcon>
                    <AccessibilityIcon />
                  </ListItemIcon>

                  <ListItemText primary="Ustawienia dostępności WCAG" />
                </ListItemButton>
              </ListItem>
            )}

            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  minHeight: "48px",
                  "&:focus-visible": {
                    outline: "3px solid",
                    outlineColor: theme.palette.primary.main,
                    outlineOffset: "2px",
                  },
                }}
              >
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>

                <ListItemText primary="Wyloguj" />
              </ListItemButton>
            </ListItem>
          </List>
        </Drawer>
      </Box>

      <WcagBox ref={wcagRef} />
    </>
  );
};

export default AppBarNav;

// import React, { useState, useEffect, useRef } from "react";
// import AppBar from "@mui/material/AppBar";
// import Button from "@mui/material/Button";
// import CameraIcon from "@mui/icons-material/PhotoCamera";
// import Box from "@mui/material/Box";
// import Toolbar from "@mui/material/Toolbar";
// import Typography from "@mui/material/Typography";
// import IconButton from "@mui/material/IconButton";
// import LogoutIcon from "@mui/icons-material/Logout";
// import MenuIcon from "@mui/icons-material/Menu";
// import { logout } from "../Slices/auth";
// import { useDispatch, useSelector } from "react-redux";
// import { Link } from "react-router-dom";
// // import Logo from "../assets/logo.png";
// import Logo from "../assets/logo.png";
// import Avatar from "@mui/material/Avatar";
// import useMediaQuery from "@mui/material/useMediaQuery";
// import { styled, useTheme } from "@mui/material/styles";

// import Drawer from "@mui/material/Drawer";
// import MuiAppBar from "@mui/material/AppBar";
// import CssBaseline from "@mui/material/CssBaseline";
// import List from "@mui/material/List";
// import Divider from "@mui/material/Divider";
// import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
// import ChevronRightIcon from "@mui/icons-material/ChevronRight";
// import ListItem from "@mui/material/ListItem";
// import ListItemButton from "@mui/material/ListItemButton";
// import ListItemIcon from "@mui/material/ListItemIcon";
// import ListItemText from "@mui/material/ListItemText";
// import InboxIcon from "@mui/icons-material/MoveToInbox";
// import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
// // import AuthVerify from "../Actions/AuthVerify";
// import ColorModeButton from "./ColorModeButton";
// import { Menu, MenuItem, Tooltip } from "@mui/material";
// import { WcagBox } from "./utilities/WcagBox";
// import AccessibilityIcon from "@mui/icons-material/Accessibility";

// const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL;

// const drawerWidth = 240;

// const pages = [
//   { name: "Moje CV", page: "/yourcv" },
//   { name: "Ścieżki kompetencyjne", page: "/sciezki" },
//   { name: "Odznaki", page: "/odznaki" },
//   { name: "Certyfikaty", page: "/certificates" },
//   { name: "Kwalifikacje", page: "/qualifications" },
//   {
//     name: "VR",
//     page: "/vr",
//     submenu: [
//       { name: "Grupy", page: "/groups" },
//       { name: "Raporty", page: "/reports" },
//       { name: "Kalendarz", page: "/vr/callendar" },
//       { name: "Kreator scenariuszy", page: "/vr/scenarios" },
//       { name: "Vr Cup", page: "/vr-cup" },
//     ],
//   },
// ];

// const guestPages = [
//   { name: "O Fundacji", page: "/" },
//   { name: "Kwalifikacje", page: "/qualifications" },
//   { name: "Twoje CV", page: "/yourcv" },
// ];

// const AppBarNav = () => {
//   const dispatch = useDispatch();
//   const { user } = useSelector((state) => state.auth);

//   const wcagRef = useRef();
//   const wcagButtonRef = useRef();

//   const handleLogout = () => {
//     console.log("LOGOUT");
//     dispatch(logout());
//   };

//   const theme = useTheme();
//   const [open, setOpen] = React.useState(false);

//   const handleDrawerOpen = () => {
//     setOpen(true);
//   };

//   const handleDrawerClose = () => {
//     setOpen(false);
//   };

//   const handleWcagClick = () => {
//     if (wcagRef.current) {
//       wcagRef.current.callChildFunction({
//         buttonRef: wcagButtonRef.current,
//       });
//     }
//   };

//   const DrawerHeader = styled("div")(({ theme }) => ({
//     display: "flex",
//     alignItems: "center",
//     padding: theme.spacing(0, 1),
//     ...theme.mixins.toolbar,
//     justifyContent: "flex-start",
//   }));

//   const mobileWidth = useMediaQuery("(max-width:900px)");

//   const [activeMenu, setActiveMenu] = useState(null);
//   const [anchorEl, setAnchorEl] = useState(null);

//   return (
//     <>
//       <AppBar className="app-bar" position={mobileWidth ? "fixed" : "relative"}>
//         {/* <AppBar className="app-bar" sx={{
//         position: 'static',
//         '@media(minWidth: 900px)' : {
//           position: 'fixed'
//         }
//       }}> */}
//         <Toolbar>
//           <Typography variant="h6" sx={{ my: 0, flexGrow: 1 }}>
//             <Link to={"/"}>
//               <img
//                 src={Logo}
//                 alt="Logo Micree"
//                 // style={{ maxWidth: "40px", marginRight: "30px" }}
//                 style={{ maxWidth: "100px", marginRight: "30px", verticalAlign: 'super' }}
//               />
//             </Link>
//           </Typography>
//           <Box sx={{ display: { xs: "none", md: "flex", mr: 4 } }}>
//             {user &&
//               (user.userRole === "user" || user.userRole === "admin") &&
//               pages.map(({ name, page, submenu }) =>
//                 submenu ? (
//                   <div
//                     key={page}
//                     onMouseLeave={() => setActiveMenu(null)}
//                     style={{ display: "inline-block" }}
//                   >
//                     <Button
//                       sx={{ color: "white" }}
//                       onMouseEnter={(e) => {
//                         setActiveMenu(name);
//                         setAnchorEl(e.currentTarget);
//                       }}
//                     >
//                       {name}
//                     </Button>

//                     <Menu
//                       anchorEl={anchorEl}
//                       open={activeMenu === name}
//                       onClose={() => setActiveMenu(null)}
//                       MenuListProps={{
//                         onMouseEnter: () => setActiveMenu(name),
//                         onMouseLeave: () => setActiveMenu(null),
//                       }}
//                       anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
//                       transformOrigin={{ vertical: "top", horizontal: "left" }}
//                     >
//                       {submenu.map(({ name: subName, page: subPage }) => (
//                         <Link
//                           key={subPage}
//                           to={subPage}
//                           style={{ textDecoration: "none", color: "black" }}
//                         >
//                           <MenuItem key={subName} onClick={() => setActiveMenu(null)}>
//                             {subName}
//                           </MenuItem>
//                         </Link>
//                       ))}
//                     </Menu>
//                   </div>
//                 ) : (
//                   <Link to={page} key={page} style={{ textDecoration: "none" }}>
//                     <Button sx={{ color: "white" }}>{name}</Button>
//                   </Link>
//                 )
//               )}
//             {user &&
//               user.userRole === "guest" &&
//               guestPages.map(({ name, page }) => (
//                 <Link to={page} key={page} style={{ textDecoration: "none" }}>
//                   <Button sx={{ my: 2, color: "white", display: "block" }}>
//                     {name}
//                   </Button>
//                 </Link>
//               ))}
//           </Box>

//           <IconButton
//             ref={wcagButtonRef}
//             size="large"
//             aria-label="WCAG settings"
//             color={theme.palette.texts.primary}
//             sx={{
//               marginX: 2,
//               width: "48px",
//               height: "48px",
//               border: "2px solid",
//               borderColor: theme.palette.texts.primary,
//               borderRadius: "8px",
//               // "&:hover": {
//               //   borderColor: theme.palette.primary.main,
//               // },
//             }}
//             onClick={handleWcagClick}
//           >
//             <AccessibilityIcon sx={{ color: theme.palette.texts.primary }} />
//           </IconButton>

//           {/* <ColorModeButton /> */}
//           <Link to="/profile" style={{ textDecoration: "none" }}>
//             <Tooltip title={`${user.firstname} ${user.lastname}`}>
//               <IconButton
//                 size="small"
//                 sx={{ ml: 2, display: mobileWidth ? "none" : null }}
//                 aria-label="Profil"
//               >
//                 <Avatar
//                   sx={{ width: 32, height: 32 }}
//                   alt={`${user.firstname}${user.lastname}`}
//                   // src={`${UPLOADS_URL}${user.profileImage}`}
//                   src={user.profileImage}
//                 ></Avatar>
//               </IconButton>
//             </Tooltip>
//           </Link>

//           {/* <AuthVerify logOut={handleLogout}> */}
//           <IconButton
//             color="inherit"
//             onClick={() => handleLogout()}
//             aria-label="Wyloguj"
//             sx={{ ml: 1, display: mobileWidth ? "none" : null }}
//           >
//             <LogoutIcon color="fontWhite" />
//           </IconButton>
//           {/* </AuthVerify> */}
//           {/* MOBILE MENU */}
//           <IconButton
//             size="large"
//             edge="start"
//             color="inherit"
//             aria-label="menu"
//             sx={{
//               mr: 2,
//               ...(open && { display: "none" }),
//               marginRight: "0px",
//               display: !mobileWidth ? "none" : null,
//             }}
//             onClick={handleDrawerOpen}
//           >
//             <MenuIcon />
//           </IconButton>
//         </Toolbar>
//       </AppBar>
//       <Box sx={{ display: "flex" }}>
//         <CssBaseline />
//         <Drawer
//           sx={{
//             width: drawerWidth,
//             flexShrink: 0,
//             "& .MuiDrawer-paper": {
//               width: drawerWidth,
//             },
//           }}
//           variant="persistent"
//           anchor="right"
//           open={open}
//         >
//           <DrawerHeader>
//             <IconButton
//               onClick={handleDrawerClose}
//               aria-label="Zamknij menu"
//             >
//               {theme.direction === "rtl" ? (
//                 <ChevronLeftIcon />
//               ) : (
//                 <ChevronRightIcon />
//               )}
//             </IconButton>
//           </DrawerHeader>
//           <Divider />
//           <List>
//             {user &&
//               user.userRole === "user" &&
//               pages.map(({ name, page }) => (
//                 <Link to={page} key={page} style={{ textDecoration: "none" }}>
//                   <ListItemButton
//                     onClick={() => setOpen(false)}
//                     sx={{ my: 2, color: "#474c55", display: "block" }}
//                   >
//                     {name}
//                   </ListItemButton>
//                 </Link>
//               ))}
//             {user &&
//               user.userRole === "guest" &&
//               guestPages.map(({ name, page }) => (
//                 <Link to={page} key={page} style={{ textDecoration: "none" }}>
//                   <ListItemButton
//                     onClick={() => setOpen(false)}
//                     sx={{ my: 2, color: "#474c55", display: "block" }}
//                   >
//                     {name}
//                   </ListItemButton>
//                 </Link>
//               ))}
//           </List>
//           <Divider />
//           <List>
//             <ListItem disablePadding>
//               <Link
//                 to="/profile"
//                 style={{ textDecoration: "none" }}
//                 onClick={() => setOpen(false)}
//               >
//                 <ListItemButton>
//                   <ListItemIcon>
//                     <ManageAccountsIcon sx={{ mr: 1 }} />
//                     Twój Profil
//                   </ListItemIcon>
//                   <ListItemText />
//                 </ListItemButton>
//               </Link>
//             </ListItem>

//             {mobileWidth && (
//               <IconButton
//                 ref={wcagButtonRef}
//                 size="large"
//                 aria-label="show 17 new notifications"
//                 color={theme.palette.texts.primary}
//                 sx={{
//                   marginX: 2,
//                   width: "48px",
//                   height: "48px",
//                   border: "1px solid",
//                   borderColor: theme.palette.divider,
//                   borderRadius: "8px",
//                   // "&:hover": {
//                   //   borderColor: theme.palette.primary.main,
//                   // },
//                 }}
//                 onClick={handleWcagClick}
//               >
//                 <AccessibilityIcon />
//               </IconButton>
//             )}

//             <ListItem disablePadding>
//               {/* <AuthVerify logOut={handleLogout}> */}
//               <ListItemButton onClick={() => handleLogout()}>
//                 <ListItemIcon>
//                   <LogoutIcon sx={{ mr: 1 }} />
//                   Wyloguj
//                 </ListItemIcon>
//                 <ListItemText />
//               </ListItemButton>
//               {/* </AuthVerify> */}
//             </ListItem>
//           </List>
//         </Drawer>
//       </Box>
//       <WcagBox ref={wcagRef} />
//     </>
//   );
// };

// export default AppBarNav;

// // import React, { useState, useEffect } from "react";
// // import AppBar from "@mui/material/AppBar";
// // import Button from "@mui/material/Button";
// // import CameraIcon from "@mui/icons-material/PhotoCamera";
// // import Box from "@mui/material/Box";
// // import Toolbar from "@mui/material/Toolbar";
// // import Typography from "@mui/material/Typography";
// // import IconButton from "@mui/material/IconButton";
// // import LogoutIcon from "@mui/icons-material/Logout";
// // import MenuIcon from "@mui/icons-material/Menu";
// // import { logout } from "../Slices/auth";
// // import { useDispatch, useSelector } from "react-redux";
// // import { Link } from "react-router-dom";
// // // import Logo from "../assets/logo.png";
// // import Logo from "../assets/logo.png";
// // import Avatar from "@mui/material/Avatar";
// // import useMediaQuery from "@mui/material/useMediaQuery";
// // import { styled, useTheme } from "@mui/material/styles";

// // import Drawer from "@mui/material/Drawer";
// // import MuiAppBar from "@mui/material/AppBar";
// // import CssBaseline from "@mui/material/CssBaseline";
// // import List from "@mui/material/List";
// // import Divider from "@mui/material/Divider";
// // import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
// // import ChevronRightIcon from "@mui/icons-material/ChevronRight";
// // import ListItem from "@mui/material/ListItem";
// // import ListItemButton from "@mui/material/ListItemButton";
// // import ListItemIcon from "@mui/material/ListItemIcon";
// // import ListItemText from "@mui/material/ListItemText";
// // import InboxIcon from "@mui/icons-material/MoveToInbox";
// // import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
// // import AuthVerify from "../Actions/AuthVerify";
// // import ColorModeButton from "./ColorModeButton";
// // import { Tooltip } from "@mui/material";

// // const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL;

// // const drawerWidth = 240;

// // const pages = [
// //   { name: "Odznaki", page: "/odznaki" },
// //   { name: "Ścieżki kompetencyjne", page: "/sciezki" },
// //   { name: "Certyfikaty", page: "/certificates" },
// //   { name: "Kwalifikacje", page: "/qualifications" },
// //   { name: "Moje CV", page: "/yourcv" },
// // ];

// // const guestPages = [
// //   { name: "O Fundacji", page: "/" },
// //   { name: "Kwalifikacje", page: "/qualifications" },
// //   { name: "Twoje CV", page: "/yourcv" },
// // ];

// // const AppBarNav = () => {
// //   const dispatch = useDispatch();
// //   const { user } = useSelector((state) => state.auth);
// //   const handleLogout = () => {
// //     console.log("LOGOUT");
// //     dispatch(logout());
// //   };

// //   const theme = useTheme();
// //   const [open, setOpen] = React.useState(false);

// //   const handleDrawerOpen = () => {
// //     setOpen(true);
// //   };

// //   const handleDrawerClose = () => {
// //     setOpen(false);
// //   };

// //   const DrawerHeader = styled("div")(({ theme }) => ({
// //     display: "flex",
// //     alignItems: "center",
// //     padding: theme.spacing(0, 1),
// //     // necessary for content to be below app bar
// //     ...theme.mixins.toolbar,
// //     justifyContent: "flex-start",
// //   }));

// //   const mobileWidth = useMediaQuery("(max-width:900px)");

// //   return (
// //     <>
// //       <AppBar className="app-bar" position={mobileWidth ? "fixed" : "relative"}>
// //         {/* <AppBar className="app-bar" sx={{
// //         position: 'static',
// //         '@media(minWidth: 900px)' : {
// //           position: 'fixed'
// //         }
// //       }}> */}
// //         <Toolbar>
// //           <Typography variant="h6" sx={{ my: 0, flexGrow: 1 }}>
// //             <Link to={"/"}>
// //               <img
// //                 src={Logo}
// //                 alt="Logo"
// //                 // style={{ maxWidth: "40px", marginRight: "30px" }}
// //                 style={{ maxWidth: "100px", marginRight: "30px" }}
// //               />
// //             </Link>
// //           </Typography>
// //           <Box sx={{ display: { xs: "none", md: "flex", mr: 4 } }}>
// //             {user &&
// //               user.userRole === "user" &&
// //               pages.map(({ name, page }) => (
// //                 <Link to={page} key={page} style={{ textDecoration: "none" }}>
// //                   <Button sx={{ my: 2, color: "white", display: "block" }}>
// //                     {name}
// //                   </Button>
// //                 </Link>
// //               ))}
// //             {user &&
// //               user.userRole === "guest" &&
// //               guestPages.map(({ name, page }) => (
// //                 <Link to={page} key={page} style={{ textDecoration: "none" }}>
// //                   <Button sx={{ my: 2, color: "white", display: "block" }}>
// //                     {name}
// //                   </Button>
// //                 </Link>
// //               ))}
// //           </Box>
// //           <ColorModeButton />
// //           <Link to="/profile" style={{ textDecoration: "none" }}>
// //             <Tooltip title={`${user.firstname} ${user.lastname}`}>
// //               <IconButton
// //                 size="small"
// //                 sx={{ ml: 2, display: mobileWidth ? "none" : null }}
// //               >
// //                 <Avatar
// //                   sx={{ width: 32, height: 32 }}
// //                   alt={`${user.firstname}${user.lastname}`}
// //                   // src={`${UPLOADS_URL}${user.profileImage}`}
// //                   src={user.profileImage}
// //                 ></Avatar>
// //               </IconButton>
// //             </Tooltip>
// //           </Link>
// //           <AuthVerify logOut={handleLogout}>
// //             <IconButton
// //               color="inherit"
// //               onClick={() => handleLogout()}
// //               sx={{ ml: 1, display: mobileWidth ? "none" : null }}
// //             >
// //               <LogoutIcon color="fontWhite" />
// //             </IconButton>
// //           </AuthVerify>
// //           {/* MOBILE MENU */}
// //           <IconButton
// //             size="large"
// //             edge="start"
// //             color="inherit"
// //             aria-label="menu"
// //             sx={{
// //               mr: 2,
// //               ...(open && { display: "none" }),
// //               marginRight: "0px",
// //               display: !mobileWidth ? "none" : null,
// //             }}
// //             onClick={handleDrawerOpen}
// //           >
// //             <MenuIcon />
// //           </IconButton>
// //         </Toolbar>
// //       </AppBar>
// //       <Box sx={{ display: "flex" }}>
// //         <CssBaseline />
// //         <Drawer
// //           sx={{
// //             width: drawerWidth,
// //             flexShrink: 0,
// //             "& .MuiDrawer-paper": {
// //               width: drawerWidth,
// //             },
// //           }}
// //           variant="persistent"
// //           anchor="right"
// //           open={open}
// //         >
// //           <DrawerHeader>
// //             <IconButton onClick={handleDrawerClose}>
// //               {theme.direction === "rtl" ? (
// //                 <ChevronLeftIcon />
// //               ) : (
// //                 <ChevronRightIcon />
// //               )}
// //             </IconButton>
// //           </DrawerHeader>
// //           <Divider />
// //           <List>
// //             {user &&
// //               user.userRole === "user" &&
// //               pages.map(({ name, page }) => (
// //                 <Link to={page} key={page} style={{ textDecoration: "none" }}>
// //                   <ListItemButton
// //                     onClick={() => setOpen(false)}
// //                     sx={{ my: 2, color: "#474c55", display: "block" }}
// //                   >
// //                     {name}
// //                   </ListItemButton>
// //                 </Link>
// //               ))}
// //             {user &&
// //               user.userRole === "guest" &&
// //               guestPages.map(({ name, page }) => (
// //                 <Link to={page} key={page} style={{ textDecoration: "none" }}>
// //                   <ListItemButton
// //                     onClick={() => setOpen(false)}
// //                     sx={{ my: 2, color: "#474c55", display: "block" }}
// //                   >
// //                     {name}
// //                   </ListItemButton>
// //                 </Link>
// //               ))}
// //           </List>
// //           <Divider />
// //           <List>
// //             <ListItem disablePadding>
// //               <Link
// //                 to="/profile"
// //                 style={{ textDecoration: "none" }}
// //                 onClick={() => setOpen(false)}
// //               >
// //                 <ListItemButton>
// //                   <ListItemIcon>
// //                     <ManageAccountsIcon sx={{ mr: 1 }} />
// //                     Twój Profil
// //                   </ListItemIcon>
// //                   <ListItemText />
// //                 </ListItemButton>
// //               </Link>
// //             </ListItem>
// //             <ListItem disablePadding>
// //               <AuthVerify logOut={handleLogout}>
// //                 <ListItemButton onClick={() => handleLogout()}>
// //                   <ListItemIcon>
// //                     <LogoutIcon sx={{ mr: 1 }} />
// //                     Wyloguj
// //                   </ListItemIcon>
// //                   <ListItemText />
// //                 </ListItemButton>
// //               </AuthVerify>
// //             </ListItem>
// //           </List>
// //         </Drawer>
// //       </Box>
// //     </>
// //   );
// // };

// // export default AppBarNav;
